import { PencilIcon } from '@heroicons/react/24/outline'
import Actions from 'Flux/index.js'
import { useGetAuthenticatedUser, useInvites } from 'Flux/Selector/index.js'
import { invitationSlice } from 'Flux/Slice/Invitation/InvitationSlice.js'
import type { IOrder } from 'Model/index.js'
import type { IOrderCompletedNotification } from 'Model/Invite/IInvite.js'
import type { ChangeEvent } from 'react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isValidEmail } from '../../../../Util/EmailUtil.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'

export interface ReportEmailModalProps {
  order: IOrder
}

export const ReportEmailModal = (props: ReportEmailModalProps & BaseModalProps) => {
  const [reportEmail, setReportEmail] = useState('')
  const [mailContent, setMailContent] = useState('')
  const [notificationId, setNotificationId] = useState<number | 'new'>()
  const dispatch = useDispatch()
  const orderCompletedNotifications = useInvites()
  const user = useGetAuthenticatedUser()

  const isDuplicate = (email: string) => {
    return (
      orderCompletedNotifications !== 'LOADING' &&
      (orderCompletedNotifications.send?.orderCompletedNotifications?.find(
        (notification) => notification.email === email && notification.orderId == props.order.id
      ) !== undefined ||
        orderCompletedNotifications.received?.orderCompletedNotifications?.find(
          (notification) => notification.email === email && notification.orderId == props.order.id
        ) !== undefined)
    )
  }

  useEffect(() => {
    if (props.isOpen) {
      dispatch(Actions[invitationSlice.name].getAllInvitesRequest())
    }
  }, [props.isOpen])

  if (!user) return <></>

  const handleEmailInputChanged = (v: ChangeEvent<HTMLInputElement>) => {
    setReportEmail(v.target.value)
  }

  const handleMailContentChanged = (v: ChangeEvent<HTMLTextAreaElement>) => {
    setMailContent(v.target.value)
  }

  const handleEditNotification = (notification: IOrderCompletedNotification) => {
    setMailContent(notification.additionalText)
    setReportEmail(notification.email)
    setNotificationId(notification.id)
  }

  const handleCreateNewNotification = () => {
    setMailContent('')
    setReportEmail('')
    setNotificationId('new')
  }

  const handleOK = () => {
    if (notificationId === 'new') {
      dispatch(
        Actions[invitationSlice.name].saveNewOrderCompletedNotificationRequest({
          orderId: props.order.id,
          email: reportEmail,
          additionalText: mailContent,
          creatorId: user.id,
          creatorOrg: user.org.id,
          type: 'order-notification'
        })
      )
    } else if (typeof notificationId === 'number') {
      dispatch(
        Actions[invitationSlice.name].updateOrderCompletedNotificationRequest({
          id: notificationId,
          email: reportEmail,
          additionalText: mailContent,
          type: 'order-notification',
          creatorId: user.id,
          creatorOrg: user.org.id,
          orderId: props.order.id
        })
      )
    }
    setReportEmail('')
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="modal-box w-fit max-w-full h-auto max-h-[90vh] overflow-auto">
        <h1 className="text-2xl text-primary-600 ">Automatic Report Mail Settings</h1>
        <div>Notify users by e-mail after processing has completed</div>
        <hr className="my-2" />
        <h2 className="text-lg">Stored Order Completion Notifications</h2>
        {orderCompletedNotifications == 'LOADING' && <label className="text-neutral-400">Loading...</label>}
        {orderCompletedNotifications != 'LOADING' &&
          (orderCompletedNotifications.send?.orderCompletedNotifications?.filter((x) => x.orderId == props.order.id)?.length === 0 ||
            !orderCompletedNotifications.send?.orderCompletedNotifications?.filter((x) => x.orderId == props.order.id)) && (
            <label className="text-neutral-400">No Notifications stored</label>
          )}
        {orderCompletedNotifications != 'LOADING' &&
          orderCompletedNotifications.send?.orderCompletedNotifications
            ?.filter((x) => x.orderId == props.order.id)
            ?.map((notification, index) => (
              <div key={index} className="flex justify-between items-center p-2 border-b">
                <div className="flex gap-x-2 items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  to: <label className="text-sm">{notification.email}</label>
                </div>
                <div>
                  <button className="btn btn-ghost btn-sm text-info" onClick={() => handleEditNotification(notification)}>
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm text-error"
                    onClick={() => dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: notification.id }))}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

        <hr className="my-2" />
        {!notificationId && (
          <button className="btn btn-primary btn-sm" onClick={() => handleCreateNewNotification()}>
            create new Notification...
          </button>
        )}
        {!!notificationId && (
          <>
            <div className="flex flex-col p-2">
              <div className="flex flex-col gap-x-2">
                <label>Mail to when Order is complete:</label>
                <input
                  type="email"
                  className={`input input-bordered input-sm `}
                  onChange={handleEmailInputChanged}
                  placeholder="customer@example.com"
                  value={reportEmail}
                  required
                />
                {reportEmail && !isValidEmail(reportEmail) && (
                  <p className="text-sm text-error mt-1">Please enter a valid email address.</p>
                )}
                {reportEmail && isDuplicate(reportEmail) && (
                  <p className="text-sm text-error mt-1">E-Mail Address will already be notified.</p>
                )}
              </div>
              <div>
                <label>Message</label>
                <textarea
                  className={`textarea textarea-bordered w-full`}
                  placeholder="Type personalized message here..."
                  rows={10}
                  onChange={handleMailContentChanged}
                  value={mailContent}
                />
              </div>
            </div>
            <hr />
          </>
        )}
        <div className="mt-3 flex gap-x-2 justify-end">
          <button
            className={`btn btn-primary ${(!notificationId || isDuplicate(reportEmail) || !isValidEmail(reportEmail)) && 'btn-disabled'}`}
            onClick={handleOK}
            disabled={!notificationId || !isValidEmail(reportEmail) || isDuplicate(reportEmail)}
          >
            Save
          </button>
          <button className="btn btn-outline" onClick={() => props.onClose()}>
            Close
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
